import {
  Building2,
  FolderOpen,
  Layers,
  List,
  User,
  UserCog,
  Users,
} from 'lucide-react';
import { LinkRouter } from '@/code/lib/routes';
import type { LookupCommand } from './types';

/**
 * Shared className for the Command component styling.
 * Applied via data attributes on cmdk elements.
 */
export const CMDK_COMMAND_CLASSNAME =
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5';

// Employee page entries (EMPLOYEE_PAGE_GROUPS) live in the shared registry:
// src/code/frontend/components/employee/employeePageRegistry.ts.
// Not re-exported here — CmdKDialog (shared with RegularCmdK) imports
// CMDK_COMMAND_CLASSNAME from this file, and a re-export would pull the
// employee registry + its icons into the regular-user bundle.

export const EMPLOYEE_LOOKUP_COMMANDS: LookupCommand[] = [
  {
    label: 'lead:',
    prefix: 'lead:',
    icon: User,
    description: 'Look up lead by ID',
    getUrl: (id: string) => LinkRouter.routeForEmployeeChat(id),
  },
  {
    label: 'campaign:',
    prefix: 'campaign:',
    icon: List,
    description: 'Look up campaign by ID',
    getUrl: (id: string) => LinkRouter.routeForEmployeeCampaigns(id),
  },
  {
    label: 'leadpool:',
    prefix: 'leadpool:',
    icon: FolderOpen,
    description: 'Open leadpool by campaign name',
    getUrl: (id: string) => LinkRouter.routeForEmployeeLeadpool(id),
  },
  {
    label: 'user:',
    prefix: 'user:',
    icon: Users,
    description: 'Look up user by ID',
    getUrl: (id: string) => LinkRouter.routeForEmployeeUserDetail(id),
  },
  {
    label: 'customer:',
    prefix: 'customer:',
    icon: Building2,
    description: 'Look up customer by name',
    getUrl: (id: string) => LinkRouter.routeForEmployeeCustomers(id),
  },
  {
    label: 'base:',
    prefix: 'base:',
    icon: Layers,
    description: 'Open a campaign base leadpool by name',
    getUrl: (id: string) =>
      LinkRouter.routeForEmployeeCampaignBaseLeads({ campaignBaseId: id }),
  },
  {
    label: 'impersonate:',
    prefix: 'impersonate:',
    icon: UserCog,
    description: 'Impersonate a user — Enter opens, ⌘Enter copies',
  },
];

/** Parse a lookup command (e.g., "lead:123") from query string */
export function parseLookupCommand(
  query: string,
): { type: string; id: string } | null {
  const match = query.match(
    /^(leadpool|lead|campaign|customer|user|base|impersonate):(.+)$/i,
  );
  if (!match || !match[1] || !match[2]) return null;
  return { type: match[1].toLowerCase(), id: match[2].trim() };
}

/** Get navigation URL for a lookup command type and ID */
export function getLookupUrl(type: string, id: string): string | null {
  const command = EMPLOYEE_LOOKUP_COMMANDS.find(
    (c) => c.prefix.toLowerCase() === `${type.toLowerCase()}:`,
  );
  if (!command?.getUrl) return null;
  return command.getUrl(id);
}

/** Format a user's display name from first/last name or email */
export function formatUserName(user: {
  firstName?: string | null;
  lastName?: string | null;
  primaryEmail?: string | null;
}): string {
  return (
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.primaryEmail ||
    'Unknown'
  );
}
